import { Capacitor } from "@capacitor/core";
import { effect, signal } from "alien-signals";
import { apiUrl, getAuthToken, getChapterDetails } from "#/client";
import { getPatterns as getReplacePatterns } from "#/feature/replace/replace-utils";
import { updateChapterCache } from "#/feature/chapter/chapter-store";
import Tts, { TtsEventType, type TtsEngine, type TtsVoice } from "./tts-plugin";

const isNative = Capacitor.getPlatform() !== "web";

const TTS_AUTO_FOLLOW_KEY = "ttsAutoFollow";
const PRIVACY_KEY = "privacyMode";

/** Whether we should auto navigate to the current chapter being played. */
export const autoFollowSignal = signal<boolean>(
  typeof localStorage !== "undefined" && localStorage.getItem(TTS_AUTO_FOLLOW_KEY) === "true",
);

/** The last url to which the auto navigation was done. */
export const lastAutoFollowUrlSignal = signal<string | null>(null);

/** The current url of the app. */
export const currentUrlSignal = signal<string>(
  typeof window !== "undefined" ? window.location.pathname : "/",
);

effect(() => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(TTS_AUTO_FOLLOW_KEY, autoFollowSignal().toString());
});

// Auto-follow deactivation: disable when external navigation is detected.
effect(() => {
  const currentUrl = currentUrlSignal();
  const lastAutoFollowUrl = lastAutoFollowUrlSignal();
  const autoFollow = autoFollowSignal();

  if (!autoFollow) return;

  if (lastAutoFollowUrl && currentUrl !== lastAutoFollowUrl) {
    autoFollowSignal(false);
  }
});

export const isPlayingSignal = signal(false);
export const serviceStoppedSignal = signal(true);
export const currentBookIdSignal = signal<number | null>(null);
export const currentChapterIdSignal = signal<number | null>(null);
export const currentTitleSignal = signal<string | null>(null);
export const currentIndexSignal = signal(0);
export const rateSignal = signal(1);
export const pitchSignal = signal(1);
export const sentenceDelaySignal = signal(300);
export const paragraphDelaySignal = signal(600);
export const engineSignal = signal<string | null>(null);
export const voiceSignal = signal<string | null>(null);
export const isTtsSupportedSignal = signal(isNative);
export const inactivityChapterLookbackSignal = signal(2);

export const privacySignal = signal<boolean>(
  typeof localStorage !== "undefined" && localStorage.getItem(PRIVACY_KEY) === "true",
);

export function setPrivacyMode(enabled: boolean): void {
  privacySignal(enabled);
}

effect(() => {
  const enabled = privacySignal();
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(PRIVACY_KEY, enabled.toString());
  }
  if (isTtsSupportedSignal()) {
    void Tts.setPrivacyMode({ enabled }).catch((error) => {
      console.error("Failed to set privacy mode on native", error);
    });
  }
});

export async function syncState(): Promise<void> {
  try {
    const data = await Tts.getState();
    isPlayingSignal(data.isPlaying);
    serviceStoppedSignal(data.serviceStopped);
    currentBookIdSignal(data.bookId === -1 ? null : data.bookId);
    currentChapterIdSignal(data.chapterId === -1 ? null : data.chapterId);
    currentTitleSignal(data.title === "" || data.title === "Webnovel TTS" ? null : data.title);
    currentIndexSignal(data.currentIndex);
    rateSignal(data.rate);
    pitchSignal(data.pitch);
    sentenceDelaySignal(data.sentenceDelay);
    paragraphDelaySignal(data.paragraphDelay);
    inactivityChapterLookbackSignal(data.inactivityChapterLookback);
    engineSignal(data.engine);
    voiceSignal(data.voice);
  } catch (error) {
    console.error("Failed to sync TTS state", error);
  }
}

/**
 * One-shot native TTS bootstrap: API/token, sync, Capacitor listeners, visibility refresh.
 * Returns a cleanup function for unmount.
 */
export function initTtsStore(): () => void {
  if (!isTtsSupportedSignal()) {
    return () => {};
  }

  void Tts.setApiUrl({ apiUrl: apiUrl("") });
  void Tts.setToken({ token: getAuthToken() });
  void syncState();

  const listeners = [
    Tts.addListener(TtsEventType.IsPlayingUpdated, (data) => {
      isPlayingSignal(data.value);
    }),
    Tts.addListener(TtsEventType.ServiceStoppedUpdated, (data) => {
      serviceStoppedSignal(data.value);
    }),
    Tts.addListener(TtsEventType.BookIdUpdated, (data) => {
      currentBookIdSignal(data.value === -1 ? null : data.value);
    }),
    Tts.addListener(TtsEventType.ChapterIdUpdated, (data) => {
      const nextId = data.value === -1 ? null : data.value;
      const bookId = currentBookIdSignal();
      const prevChapterId = currentChapterIdSignal();
      if (data.value !== -1 && data.value !== prevChapterId && bookId) {
        void getChapterDetails(bookId, data.value).then((details) =>
          updateChapterCache(bookId, data.value, details),
        );
      }
      currentChapterIdSignal(nextId);
    }),
    Tts.addListener(TtsEventType.TitleUpdated, (data) => {
      currentTitleSignal(data.value === "Webnovel TTS" ? null : data.value);
    }),
    Tts.addListener(TtsEventType.IndexUpdated, (data) => {
      currentIndexSignal(data.value);
    }),
  ];

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void syncState();
    }
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    listeners.forEach((l) => void l.then((h) => h.remove()));
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}

export async function play(
  bookId: number,
  chapterId: number,
  title: string,
  _bookName?: string,
  _coverUrl?: string,
  index?: number,
): Promise<void> {
  const isSameChapter = bookId === currentBookIdSignal() && chapterId === currentChapterIdSignal();
  const targetIndex =
    index !== undefined && index !== -1 ? index : isSameChapter ? currentIndexSignal() : 0;

  isPlayingSignal(true);
  serviceStoppedSignal(false);
  currentBookIdSignal(bookId);
  currentChapterIdSignal(chapterId);
  currentTitleSignal(title);
  currentIndexSignal(targetIndex);

  await Tts.speak({ bookId, chapterId, index: index ?? -1 });
}

export async function pause(): Promise<void> {
  await Tts.pause();
}

export async function resume(): Promise<void> {
  await Tts.resume();
}

export async function stop(): Promise<void> {
  await Tts.stop();
  isPlayingSignal(false);
  serviceStoppedSignal(true);
  currentBookIdSignal(null);
  currentChapterIdSignal(null);
  currentTitleSignal(null);
}

export async function nextSentence(): Promise<void> {
  await Tts.nextSentence();
}

export async function prevSentence(): Promise<void> {
  await Tts.prevSentence();
}

export async function setRate(rate: number): Promise<void> {
  await Tts.setRate({ rate });
  rateSignal(rate);
}

export async function setPitch(pitch: number): Promise<void> {
  await Tts.setPitch({ pitch });
  pitchSignal(pitch);
}

export async function setSentenceDelay(delay: number): Promise<void> {
  await Tts.setSentenceDelay({ delay });
  sentenceDelaySignal(delay);
}

export async function setParagraphDelay(delay: number): Promise<void> {
  await Tts.setParagraphDelay({ delay });
  paragraphDelaySignal(delay);
}

export async function setInactivityChapterLookback(lookback: number): Promise<void> {
  await Tts.setInactivityChapterLookback({ lookback });
  inactivityChapterLookbackSignal(lookback);
}

export async function getVoices(): Promise<TtsVoice[]> {
  const res = await Tts.getVoices();
  return res?.voices ?? (res as { value?: TtsVoice[] })?.value ?? [];
}

export async function setVoice(voiceId: string): Promise<void> {
  await Tts.setVoice({ voiceId });
  voiceSignal(voiceId);
}

export async function getEngines(): Promise<TtsEngine[]> {
  const res = await Tts.getEngines();
  return res?.engines ?? (res as { value?: TtsEngine[] })?.value ?? [];
}

export async function setEngine(engineId: string): Promise<void> {
  await Tts.setEngine({ engineId });
  engineSignal(engineId);
}

export async function syncSearchReplacePatterns(): Promise<void> {
  try {
    const patterns = await getReplacePatterns();
    await Tts.setSearchReplacePatterns({ patterns });
  } catch (error) {
    console.error("Failed to sync search/replace patterns", error);
  }
}
